// Date field of the lifecycle dialogs: a native date input (keyboard and screen-reader friendly, themed by color-scheme, safe inside modal dialogs) with label, help text, minimum date and an error message wired through aria-describedby.
import { useId, type ReactNode } from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface DateFieldProps {
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  min?: string | undefined;
  description?: ReactNode;
  error?: string | null;
  required?: boolean;
}

export function DateField({
  label,
  value,
  onChange,
  min,
  description,
  error,
  required = false,
}: DateFieldProps) {
  const id = useId();
  const descriptionId = useId();
  const errorId = useId();
  const described = [description === undefined ? null : descriptionId, error ? errorId : null]
    .filter((item): item is string => item !== null)
    .join(" ");
  return (
    <div className="flex flex-col gap-[5px]">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="date"
        value={value}
        min={min}
        required={required}
        className="w-48 tabular-nums"
        aria-invalid={error ? true : undefined}
        aria-describedby={described.length > 0 ? described : undefined}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
      {description === undefined ? null : (
        <p id={descriptionId} className="m-0 text-label text-muted-foreground">
          {description}
        </p>
      )}
      {error ? (
        <p id={errorId} className="m-0 text-label text-brand-text">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function todayIso(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${String(now.getFullYear())}-${month}-${day}`;
}
